import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CategorySelect from '../components/CategorySelect';
import OfferImageSelector from '../components/OfferImageSelector';
import TripleCategorySelector from '../components/TripleCategorySelector';
import { Plus, MapPin, User, Mail, Phone, ChevronRight, Eye, EyeOff } from 'lucide-react';
import { getAllCategories, getSubcategories, getCategoryById } from '../data/categories';
import { Switch } from '../components/ui/switch';
import AddressAutocomplete from '../components/AddressAutocomplete';
import api from '../api/axios';

const AddOffer = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const scrollPositionRef = useRef(0);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    categories: [
      { category: '', subcategory: '' },
      { category: '', subcategory: '' },
      { category: '', subcategory: '' }
    ],
    address: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    image_url: ''
  });
  const [privacy, setPrivacy] = useState({
    showExactAddress: true,
    showEmail: true,
    showPhone: true
  });

  // Prevent unwanted scroll when Select opens
  useEffect(() => {
    const handleScroll = () => {
      if (document.activeElement?.getAttribute('role') === 'combobox') {
        window.scrollTo(0, scrollPositionRef.current);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: false });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const allCategories = getAllCategories();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Vérifier qu'au moins une catégorie est sélectionnée
    const selectedCategories = formData.categories.filter(c => c.category);
    if (!formData.title || !formData.description || selectedCategories.length === 0 || !formData.address) {
      toast.error('Erreur', { description: 'Veuillez remplir tous les champs obligatoires (au moins 1 catégorie)' });
      return;
    }

    setLoading(true);
    try {
      // Créer une offre pour chaque catégorie sélectionnée
      const promises = selectedCategories.map(async (catData) => {
        const categoryData = getCategoryById(catData.category);
        const offerData = {
          title: formData.title,
          description: formData.description,
          category: catData.category,
          subcategory: catData.subcategory || categoryData?.name || catData.category,
          address: formData.address,
          contact: {
            name: formData.contactName || 'Non renseigné',
            email: formData.contactEmail || 'non-renseigne@example.com',
            phone: formData.contactPhone || 'Non renseigné'
          },
          privacy_settings: {
            show_exact_address: privacy.showExactAddress,
            show_email: privacy.showEmail,
            show_phone: privacy.showPhone
          }
        };

        return api.post('/offers', offerData);
      });

      await Promise.all(promises);
      
      toast.success('Offre(s) soumise(s) avec succès !', {
        description: `${selectedCategories.length} offre(s) créée(s) et en attente de modération`
      });
      navigate('/carte');
    } catch (error) {
      toast.error('Erreur', {
        description: error.response?.data?.detail || 'Une erreur est survenue'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      if (field === 'category') {
        newData.subcategory = '';
      }
      return newData;
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-100 via-yellow-100 to-pink-200">
      <Navbar />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-600 via-red-500 to-yellow-500 rounded-2xl mb-4">
              <Plus className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-slate-800 mb-3">
              Proposer une Offre Solidaire
            </h1>
            <p className="text-gray-600 text-lg">
              Partagez votre initiative avec la communauté
            </p>
          </div>

          <Card className="border-none shadow-xl">
            <CardHeader>
              <CardTitle>Informations de l'offre</CardTitle>
              <CardDescription>Les champs marqués d'un * sont obligatoires</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="title">Titre de l'offre *</Label>
                  <Input
                    id="title"
                    placeholder="Ex: Atelier de bricolage solidaire"
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    placeholder="Décrivez votre offre en détail..."
                    rows={5}
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    required
                  />
                </div>

                {/* Sélecteur d'image */}
                <Card className="border-2 border-purple-200 bg-purple-50/30">
                  <CardContent className="pt-6">
                    <OfferImageSelector
                      category={formData.category}
                      selectedImage={formData.image_url}
                      onImageSelect={(imageUrl) => handleChange('image_url', imageUrl)}
                    />
                  </CardContent>
                </Card>

                <div className="space-y-4 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border-2 border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className="text-purple-700 border-purple-300">
                      Sélection de catégories (jusqu'à 3)
                    </Badge>
                  </div>

                  <TripleCategorySelector
                    categories={formData.categories}
                    onChange={(categories) => setFormData(prev => ({ ...prev, categories }))}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Adresse *</Label>
                  <AddressAutocomplete
                    value={formData.address}
                    onChange={(value) => handleChange('address', value)}
                    placeholder="15 Rue des Jardins, 94230 Cachan"
                    required
                  />
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg mt-2">
                    <div className="flex items-center gap-2">
                      {privacy.showExactAddress ? <Eye className="w-4 h-4 text-blue-600" /> : <EyeOff className="w-4 h-4 text-gray-400" />}
                      <Label htmlFor="address-privacy" className="cursor-pointer text-sm">
                        Afficher l'adresse exacte
                      </Label>
                    </div>
                    <Switch
                      id="address-privacy"
                      checked={privacy.showExactAddress}
                      onCheckedChange={(checked) => setPrivacy(prev => ({ ...prev, showExactAddress: checked }))}
                    />
                  </div>
                  {!privacy.showExactAddress && (
                    <p className="text-xs text-gray-500 italic">
                      Seule votre ville sera affichée sur la carte
                    </p>
                  )}
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4">Informations de contact (optionnel)</h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="contactName">Nom du contact</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="contactName"
                          placeholder="Votre nom"
                          className="pl-10"
                          value={formData.contactName}
                          onChange={(e) => handleChange('contactName', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="contactEmail">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="contactEmail"
                          type="email"
                          placeholder="votre@email.com"
                          className="pl-10"
                          value={formData.contactEmail}
                          onChange={(e) => handleChange('contactEmail', e.target.value)}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg mt-2">
                        <div className="flex items-center gap-2">
                          {privacy.showEmail ? <Eye className="w-4 h-4 text-blue-600" /> : <EyeOff className="w-4 h-4 text-gray-400" />}
                          <Label htmlFor="email-privacy" className="cursor-pointer text-sm">
                            Afficher l'email publiquement
                          </Label>
                        </div>
                        <Switch
                          id="email-privacy"
                          checked={privacy.showEmail}
                          onCheckedChange={(checked) => setPrivacy(prev => ({ ...prev, showEmail: checked }))}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="contactPhone">Téléphone</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="contactPhone"
                          type="tel"
                          placeholder="06 12 34 56 78"
                          className="pl-10"
                          value={formData.contactPhone}
                          onChange={(e) => handleChange('contactPhone', e.target.value)}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg mt-2">
                        <div className="flex items-center gap-2">
                          {privacy.showPhone ? <Eye className="w-4 h-4 text-blue-600" /> : <EyeOff className="w-4 h-4 text-gray-400" />}
                          <Label htmlFor="phone-privacy" className="cursor-pointer text-sm">
                            Afficher le téléphone publiquement
                          </Label>
                        </div>
                        <Switch
                          id="phone-privacy"
                          checked={privacy.showPhone}
                          onCheckedChange={(checked) => setPrivacy(prev => ({ ...prev, showPhone: checked }))}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 pt-6">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => navigate('/carte')}
                    disabled={loading}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-purple-600 via-red-500 to-yellow-500 hover:from-purple-700 hover:via-red-600 hover:to-yellow-600"
                    disabled={loading}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {loading ? 'Envoi en cours...' : 'Soumettre l\'offre'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AddOffer;
